const DEFAULTS = {
    minSamplesForTail: 3,
    longTailRatio: 5,
    minBusiestCount: 50,
    minBusiestRatio: 2.5,
    minHotspotShare: 0.4,
    minSamplesForOutlier: 5,
    outlierRatio: 3,
};
/**
 * Surface high-signal observations from a session's per-method aggregates.
 * Returns at most one insight per kind, ordered by descending importance —
 * UI renders them as a small "INSIGHTS" callout block at the top of the
 * performance panel; CLI prints them above the per-method table.
 *
 * Pure — no DB, no time, no IO. Tested via `perf-insights.test.ts`.
 */
export function buildPerformanceInsights(messages, perMethod, cfg = {}) {
    const config = { ...DEFAULTS, ...cfg };
    const out = [];
    const totalWallTime = perMethod.reduce((acc, m) => acc + (m.totalLatencyMs ?? 0), 0);
    // HOTSPOT — single method consuming the largest share of measured wall time.
    if (totalWallTime > 0) {
        const ranked = perMethod
            .filter((m) => m.totalLatencyMs !== null && m.totalLatencyMs > 0)
            .sort((a, b) => (b.totalLatencyMs ?? 0) - (a.totalLatencyMs ?? 0));
        const top = ranked[0];
        if (top && top.totalLatencyMs !== null) {
            const share = top.totalLatencyMs / totalWallTime;
            if (share >= config.minHotspotShare) {
                out.push({
                    kind: 'hotspot',
                    summary: `${top.method} consumed ${formatMs(top.totalLatencyMs)} of total wall time`,
                    detail: `${Math.round(share * 100)}% of ${formatMs(totalWallTime)} sampled latency`,
                    methods: [top.method],
                });
            }
        }
    }
    // LONG TAIL — methods with disproportionate p99/p50 (sometimes slow).
    const longTailCandidates = perMethod
        .filter((m) => m.sampleSize >= config.minSamplesForTail &&
        m.p50 !== null &&
        m.p99 !== null &&
        m.p50 > 0 &&
        m.p99 / m.p50 >= config.longTailRatio)
        .sort((a, b) => (b.p99 ?? 0) / (b.p50 ?? 1) - (a.p99 ?? 0) / (a.p50 ?? 1));
    const tail = longTailCandidates[0];
    if (tail && tail.p99 !== null && tail.p50 !== null) {
        const ratio = Math.round(tail.p99 / tail.p50);
        out.push({
            kind: 'long-tail',
            summary: `${tail.method} has a long tail — p99 is ${ratio}× the p50`,
            detail: `p50 ${formatMs(tail.p50)} · p99 ${formatMs(tail.p99)} · ${tail.sampleSize} samples`,
            methods: [tail.method],
        });
    }
    // OUTLIER — a single slow call that dominates its method's percentile.
    // Inserted before BUSIEST so the order in the panel is severity-aware:
    // wall-time hotspot → distribution tail → single bad call → volume noise
    // → errors.
    const outlierCandidate = [...perMethod]
        .filter((m) => m.sampleSize >= config.minSamplesForOutlier &&
        m.p99 !== null &&
        m.max !== null &&
        m.p99 > 0 &&
        m.max / m.p99 >= config.outlierRatio)
        .sort((a, b) => (b.max ?? 0) - (a.max ?? 0))[0];
    if (outlierCandidate && outlierCandidate.max !== null && outlierCandidate.p99 !== null) {
        const ratio = Math.round(outlierCandidate.max / outlierCandidate.p99);
        out.push({
            kind: 'outlier',
            summary: `${outlierCandidate.method} had one ${formatMs(outlierCandidate.max)} call — ${ratio}× its p99`,
            detail: `p99 ${formatMs(outlierCandidate.p99)} · ${outlierCandidate.sampleSize} samples`,
            methods: [outlierCandidate.method],
        });
    }
    // BUSIEST — only when a method genuinely dominates traffic. Requires
    // both an absolute count threshold AND a ratio over the next-busiest
    // method, so the typical ACP session/update chunking pattern doesn't
    // produce a noisy insight ("session/update sent 20 notifications").
    const ranked = [...perMethod].sort((a, b) => b.count - a.count);
    const busiest = ranked[0];
    const second = ranked[1];
    if (busiest && busiest.count >= config.minBusiestCount) {
        const ratio = second && second.count > 0 ? busiest.count / second.count : Infinity;
        if (ratio >= config.minBusiestRatio) {
            const noun = busiest.kind === 'notification' ? 'notifications' : 'calls';
            out.push({
                kind: 'busiest',
                summary: `${busiest.method} sent ${busiest.count} ${noun}`,
                detail: second
                    ? `${ratio.toFixed(1)}× the next-busiest method (${second.method} · ${second.count})`
                    : 'no other methods to compare against',
                methods: [busiest.method],
            });
        }
    }
    // ERRORS — methods that produced a kind='error' frame. Walks messages
    // to attribute errors back to their paired request's method (errors
    // themselves don't carry method).
    const errorMethods = collectErrorMethods(messages);
    if (errorMethods.length > 0) {
        const shown = errorMethods.slice(0, 3);
        const more = errorMethods.length > 3 ? ` (+${errorMethods.length - 3} more)` : '';
        out.push({
            kind: 'errors',
            summary: `${errorMethods.length} method${errorMethods.length === 1 ? '' : 's'} returned an error`,
            detail: shown.join(', ') + more,
            methods: errorMethods,
        });
    }
    return out;
}
function collectErrorMethods(messages) {
    const requestMethods = new Map(); // rpcId → method
    const errored = new Set();
    for (const m of messages) {
        const id = m.rpcId === undefined || m.rpcId === null ? null : String(m.rpcId);
        if (m.kind === 'request' && m.method && id !== null) {
            requestMethods.set(id, m.method);
        }
        else if (m.kind === 'error' && id !== null) {
            const method = requestMethods.get(id);
            if (method)
                errored.add(method);
        }
    }
    return [...errored].sort();
}
function formatMs(ms) {
    if (ms < 1000)
        return `${Math.round(ms)}ms`;
    if (ms < 10_000)
        return `${(ms / 1000).toFixed(2)}s`;
    if (ms < 60_000)
        return `${(ms / 1000).toFixed(1)}s`;
    const m = Math.floor(ms / 60_000);
    const s = Math.round((ms % 60_000) / 1000);
    return `${m}m${s}s`;
}
//# sourceMappingURL=perf-insights.js.map